/**
 * CursorInputContext — exposes the data and handlers needed by the
 * cursor-anchored input bar (rendered inside the workspace view) to the
 * subtree below `MasterLayout`.
 *
 * Why a context? The workspace view is rendered as `children` of
 * `MasterLayout` from the workspace page, so the natural way to surface
 * master-layout-owned values (input agents, slash commands, model pill
 * data, the chat-send function) into the view is via a provider placed at
 * the master-layout level. This avoids drilling through the page and keeps
 * the wiring local to the layout.
 */
import { createContext, useContext, useEffect } from "react";
import type { ReactNode } from "react";
import type { InputAgent } from "@/components/panels/agent-stack";
import type { SlashCommand } from "@/lib/slash-commands";
import type {
  InputBarFocusedAgent,
  InputBarProvider,
} from "@/components/panels/input-bar-model-pill";
import type { EffortChoice, RuntimeKey } from "@/lib/model-registry";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import type { InputBarSubmitResult } from "@/components/panels/input-bar-types";
import type { AgentAvatarStyleKey } from "@/lib/dicebear-styles";
import type { MentionedFrame } from "@/lib/frame-mentions";
import type { ScreenRecordingsContext } from "@/lib/screen-recordings-context";
import { publishCursorInputContextBridge } from "@/hooks/use-cursor-input-context-bridge";
import type {
  InputEffortPickOptions,
  InputModelPickOptions,
} from "@/hooks/use-input-model-actions";

export interface CursorInputContextValue {
  /** Submit a chat message at the canvas-center default position.
   *  - `value`: raw user input (may contain @mentions and slash commands).
   *  - `agentKey`: lowercase agent name picked in the cursor bar's stack;
   *    overrides the global selected-agent fallback so the stack actually
   *    routes the prompt instead of inheriting the big-input-bar's choice. */
  onSubmit: (
    value: string,
    agentKey?: string,
    options?: {
      conversationFrameId?: string;
      conversationId?: string;
      executionId?: string;
      runtimeSessionId?: string;
      composerId?: string;
      position?: { x: number; y: number };
      startNewConversation?: boolean;
      /** Pinned to this conversation: never moved to a new one by topic. */
      keepConversation?: boolean;
      ignoreInputBarSessionState?: boolean;
      mentionedFrames?: MentionedFrame[];
      textOnly?: boolean;
      /** textOnly strips attachments, except when this is set: native child
       *  follow-ups keep literal text + no auto-capture but may carry images
       *  (the backend enforces per-provider support). */
      allowImages?: boolean;
      skipAutoCapture?: boolean;
      screenRecordings?: ScreenRecordingsContext | null;
      recordingImages?: ImageAttachment[];
      nativeChildInstruction?: boolean;
    },
  ) => InputBarSubmitResult | Promise<InputBarSubmitResult>;
  inputAgents: InputAgent[];
  slashCommands: SlashCommand[];
  focusedAgentInfo: InputBarFocusedAgent | null;
  /** Resolve model-pill data for any project agent id — lets the cursor
   *  bar show the model + context ring of its locally-selected agent
   *  rather than always mirroring the global input bar's selection. */
  getFocusedAgentInfo: (agentId: string | null) => InputBarFocusedAgent | null;
  /** True when the companion is disconnected — drives the wrench chip
   *  shown at the right edge of the bar (replaces the model pill). */
  companionDisconnected: boolean;
  /** Open the Settings → Companion tab (used by the wrench chip). */
  onFixCompanion: () => void;
  /** Pending image attachments (paste / drag-drop). Shared with the
   *  global input bar so attachments survive switching between the two
   *  bars and the cursor submit handler picks them up. */
  attachedImages: ImageAttachment[];
  /** Attach images already uploaded during a claimed dictation. */
  onAttachImages?: (images: ImageAttachment[]) => void;
  /** Upload + attach a pasted/dropped image. */
  onImageFile: (file: File, source: "paste" | "drop") => void;
  /** Remove an attachment from the strip. */
  onRemoveImage: (id: string) => void;
  providers: InputBarProvider[];
  onUpdateAgentModel: (
    agentId: string,
    provider: RuntimeKey,
    runtimeId: string,
    model: string,
    options?: InputModelPickOptions,
  ) => Promise<void>;
  onUpdateAgentEffort: (
    agentId: string,
    effort: EffortChoice,
    options?: InputEffortPickOptions,
  ) => Promise<void>;
  /** Long-press → tap on an agent chip opens its settings in the right
   *  panel. Same handler the global input bar wires up. */
  onOpenSettingsForAgent: (agentId: string) => void;
  /** Same as ``onOpenSettingsForAgent`` but keyed by agent NAME — used
   *  by the cursor stall chip's "Switch model" affordance, which only
   *  has the cursor's display name to work with. Resolves to a project
   *  agent id internally. */
  onOpenSettingsForAgentName: (agentName: string) => void;
  /** Remove an agent from the project (shiver-mode delete in the stack). */
  onRemoveAgent: (agentId: string) => void;
  /** True when an execution is running in the active space — drives the
   *  send button switching to a stop button. */
  isStreaming: boolean;
  /** Stop the running execution(s) for the active space. */
  onStop: () => void;
  /** Max parallel agents (1-10) — surfaced in /max status text. */
  maxParallelAgents: number;
  /** Persist a new max-parallel-agents value. */
  onChangeMaxParallelAgents: (value: number) => void;
  /** Open project settings for an agent. */
  onAgentInspect: (templateId: string | null) => void;
  /** Multi-select agent stack — controlled selection. */
  selectedAgentIds: Set<string>;
  /** Current user's preferred agent avatar renderer. */
  agentAvatarStyleKey: AgentAvatarStyleKey;
  /** Update the controlled selection. */
  onSelectAgents: (ids: Set<string>) => void;
  /** Open the global agent-settings modal. */
  onOpenSettings: () => void;
  /** Project + space ids passed through for canvas context and agent hire. */
  projectId?: string;
  spaceId?: string;
  /** Per-summon screenshot attachment intent. Starts off and is enabled
   *  for annotation capture; screen keywords are resolved at submit time.
   *  Optional because non-desktop providers don't supply it. */
  screenshotIntent?: boolean;
  setScreenshotIntent?: (next: boolean) => void;
}

const CursorInputContext = createContext<CursorInputContextValue | null>(null);

export function CursorInputProvider({
  value,
  children,
}: {
  value: CursorInputContextValue;
  children: ReactNode;
}) {
  useEffect(() => {
    publishCursorInputContextBridge(value);
  }, [value]);

  useEffect(
    () => () => {
      publishCursorInputContextBridge(null);
    },
    [],
  );

  return (
    <CursorInputContext.Provider value={value}>
      {children}
    </CursorInputContext.Provider>
  );
}

/** Returns the cursor input context, or null when no provider is mounted. */
export function useCursorInputContext(): CursorInputContextValue | null {
  return useContext(CursorInputContext);
}
